import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { Shield, AlertTriangle, Eye, CheckCircle2, XCircle, Loader2, RefreshCw, Clock, Users, Ban, CreditCard, Scissors, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { LockedFeature } from "@/components/shared/LockedFeature";

const SEVERITY_STYLES = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-blue-100 text-blue-700 border-blue-200",
};

const SEVERITY_LABELS = { critical: "CRITICO", high: "ALTO", medium: "MEDIO", low: "BAIXO" };

const RULE_ICONS = {
  excessive_cancellations: Ban,
  discount_abuse: DollarSign,
  void_after_payment: XCircle,
  unusual_hours: Clock,
  price_modification: DollarSign,
  cash_drawer_open: CreditCard,
  repeated_refunds: Scissors,
};

export default function AntiFraudPage() {
  const gate = useFeatureGate("antifurto_ia");
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [config, setConfig] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [resolveNote, setResolveNote] = useState("");
  const [resolvingId, setResolvingId] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, alertsRes, statsRes, rulesRes] = await Promise.all([
        authAxiosRef.current.get("/antitheft/config"),
        authAxiosRef.current.get("/antitheft/alerts?limit=100"),
        authAxiosRef.current.get("/antitheft/stats"),
        authAxiosRef.current.get("/antitheft/rules"),
      ]);
      setConfig(cfgRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setStats(statsRes.data);
      setRules(rulesRes.data);
    } catch { toast.error("Erro ao carregar dados"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = async (enabled) => {
    setSaving(true);
    try {
      const res = await authAxios.put("/antitheft/config", { ...config, enabled });
      setConfig(res.data);
      toast.success(enabled ? "Antifurto ativado!" : "Antifurto desativado");
    } catch { toast.error("Erro"); }
    setSaving(false);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const res = await authAxios.put("/antitheft/config", config);
      setConfig(res.data);
      toast.success("Configuracoes salvas!");
    } catch { toast.error("Erro"); }
    setSaving(false);
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await authAxios.post("/antitheft/scan");
      toast.success(`Scan completo! ${res.data.alerts_created} novos alertas detectados`);
      fetchData();
    } catch { toast.error("Erro no scan"); }
    setScanning(false);
  };

  const handleResolve = async (alertId) => {
    try {
      await authAxios.put(`/antitheft/alerts/${alertId}/resolve`, { note: resolveNote });
      toast.success("Alerta resolvido");
      setResolvingId(null);
      setResolveNote("");
      fetchData();
    } catch { toast.error("Erro"); }
  };

  const handleDismiss = async (alertId) => {
    try {
      await authAxios.put(`/antitheft/alerts/${alertId}/dismiss`);
      toast.success("Alerta descartado");
      fetchData();
    } catch { toast.error("Erro"); }
  };

  const toggleRule = (ruleId) => {
    setConfig(prev => ({
      ...prev,
      rules: { ...prev.rules, [ruleId]: !prev.rules[ruleId] },
    }));
  };

  const filteredAlerts = filter === "all"
    ? alerts
    : filter === "active"
    ? alerts.filter(a => a.status === "active")
    : alerts.filter(a => a.severity === filter);

  if (gate === "blocked") return <MainLayout><LockedFeature featureName="Antifurto IA" addonId="antifurto_ia" /></MainLayout>;
  if (loading) return <MainLayout><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div></MainLayout>;

  return (
    <MainLayout>
    <div className="space-y-6 max-w-5xl" data-testid="antifurto-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" data-testid="antifurto-title">
            <Shield className="w-5 h-5 text-red-500" /> Antifurto IA
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Monitoramento inteligente que detecta atividades suspeitas e alerta o dono em tempo real.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleScan}
            disabled={scanning}
            className="gap-2 bg-red-600 hover:bg-red-700"
            data-testid="scan-btn"
          >
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Escanear agora
          </Button>
          <Switch checked={config?.enabled || false} onCheckedChange={handleToggle} disabled={saving} data-testid="antitheft-toggle" />
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="antifurto-stats">
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-muted-foreground">Alertas ativos</p>
            <p className="text-2xl font-black text-red-600">{stats.active}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-muted-foreground">Hoje</p>
            <p className="text-2xl font-black">{stats.today}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-muted-foreground">Esta semana</p>
            <p className="text-2xl font-black">{stats.this_week}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-muted-foreground">Criticos (30d)</p>
            <p className="text-2xl font-black text-red-600">{stats.critical_30d}</p>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <p className="text-xs text-muted-foreground">Resolvidos</p>
            <p className="text-2xl font-black text-emerald-600">{stats.resolved}</p>
          </div>
        </div>
      )}

      {/* Top Employees Flagged */}
      {stats?.top_employees?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5" data-testid="flagged-employees">
          <h3 className="font-bold flex items-center gap-2 mb-3 text-red-700">
            <Users className="w-4 h-4" /> Funcionarios com mais alertas (30 dias)
          </h3>
          <div className="flex flex-wrap gap-2">
            {stats.top_employees.map((emp, i) => (
              <span key={i} className="bg-white border border-red-200 rounded-full px-3 py-1.5 text-sm">
                <span className="font-bold text-red-600 mr-1">{emp.count}x</span> {emp.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Alerts */}
      <div className="bg-white border rounded-xl">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" /> Alertas
          </h3>
          <div className="flex gap-1.5">
            {[
              { id: "all", label: "Todos" },
              { id: "active", label: "Ativos" },
              { id: "critical", label: "Criticos" },
              { id: "high", label: "Altos" },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${filter === f.id ? "bg-red-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {filteredAlerts.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Shield className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nenhum alerta encontrado</p>
            <p className="text-xs mt-1">Clique em "Escanear agora" para verificar atividades suspeitas</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredAlerts.map(alert => {
              const RuleIcon = RULE_ICONS[alert.rule_id] || AlertTriangle;
              return (
                <div key={alert.id} className={`p-4 ${alert.status === "resolved" ? "opacity-60" : ""}`} data-testid={`alert-${alert.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${alert.severity === "critical" ? "bg-red-100" : alert.severity === "high" ? "bg-orange-100" : "bg-amber-100"}`}>
                      <RuleIcon className={`w-4 h-4 ${alert.severity === "critical" ? "text-red-600" : alert.severity === "high" ? "text-orange-600" : "text-amber-600"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${SEVERITY_STYLES[alert.severity] || "bg-gray-100"}`}>
                          {SEVERITY_LABELS[alert.severity] || alert.severity}
                        </span>
                        {alert.employee_name && (
                          <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{alert.employee_name}</span>
                        )}
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${alert.status === "active" ? "bg-red-50 text-red-600" : alert.status === "resolved" ? "bg-emerald-50 text-emerald-600" : "bg-gray-50 text-gray-400"}`}>
                          {alert.status === "active" ? "ATIVO" : alert.status === "resolved" ? "RESOLVIDO" : "DESCARTADO"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800">{alert.description}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {alert.created_at ? new Date(alert.created_at).toLocaleString("pt-BR") : ""}
                      </p>
                      {alert.resolved_by && (
                        <p className="text-[10px] text-emerald-600 mt-0.5">Resolvido por {alert.resolved_by}: {alert.resolution_note}</p>
                      )}
                    </div>
                    {alert.status === "active" && (
                      <div className="flex gap-1.5 shrink-0">
                        {resolvingId === alert.id ? (
                          <div className="flex items-center gap-1.5">
                            <Input
                              value={resolveNote}
                              onChange={e => setResolveNote(e.target.value)}
                              placeholder="Nota..."
                              className="h-7 w-32 text-xs"
                              data-testid={`resolve-note-${alert.id}`}
                            />
                            <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-xs px-2" onClick={() => handleResolve(alert.id)} data-testid={`resolve-confirm-${alert.id}`}>
                              <CheckCircle2 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => setResolvingId(null)}>
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setResolvingId(alert.id)} data-testid={`resolve-btn-${alert.id}`}>
                              <CheckCircle2 className="w-3 h-3" /> Resolver
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-400" onClick={() => handleDismiss(alert.id)} data-testid={`dismiss-btn-${alert.id}`}>
                              Ignorar
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rules Configuration */}
      {config?.enabled && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="rules-config">
          <h3 className="font-bold flex items-center gap-2">
            <Eye className="w-4 h-4 text-red-500" /> Regras de Monitoramento
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {rules.map(rule => {
              const RuleIcon = RULE_ICONS[rule.id] || AlertTriangle;
              return (
                <div key={rule.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`rule-${rule.id}`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <RuleIcon className="w-4 h-4 text-gray-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{rule.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{rule.desc}</p>
                    </div>
                  </div>
                  <Switch
                    checked={config.rules?.[rule.id] ?? true}
                    onCheckedChange={() => toggleRule(rule.id)}
                    data-testid={`toggle-rule-${rule.id}`}
                  />
                </div>
              );
            })}
          </div>

          <div className="grid sm:grid-cols-3 gap-4 pt-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Desconto maximo sem aprovacao (%)</label>
              <Input
                type="number"
                value={config.max_discount_pct || 15}
                onChange={e => setConfig(prev => ({ ...prev, max_discount_pct: parseInt(e.target.value) || 0 }))}
                data-testid="max-discount-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Horario inicio</label>
              <Input
                type="time"
                value={config.working_hours?.start || "10:00"}
                onChange={e => setConfig(prev => ({ ...prev, working_hours: { ...prev.working_hours, start: e.target.value } }))}
                data-testid="work-start-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Horario fim</label>
              <Input
                type="time"
                value={config.working_hours?.end || "23:00"}
                onChange={e => setConfig(prev => ({ ...prev, working_hours: { ...prev.working_hours, end: e.target.value } }))}
                data-testid="work-end-input"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <div className="flex items-center gap-2">
              <Switch
                checked={config.alert_dashboard ?? true}
                onCheckedChange={v => setConfig(prev => ({ ...prev, alert_dashboard: v }))}
                data-testid="alert-dashboard-toggle"
              />
              <label className="text-xs">Dashboard</label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.alert_whatsapp ?? true}
                onCheckedChange={v => setConfig(prev => ({ ...prev, alert_whatsapp: v }))}
                data-testid="alert-whatsapp-toggle"
              />
              <label className="text-xs">WhatsApp</label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.alert_email ?? true}
                onCheckedChange={v => setConfig(prev => ({ ...prev, alert_email: v }))}
                data-testid="alert-email-toggle"
              />
              <label className="text-xs">Email</label>
            </div>
          </div>

          <Button onClick={handleSaveConfig} disabled={saving} className="bg-red-600 hover:bg-red-700 gap-2" data-testid="save-config-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            Salvar configuracoes
          </Button>
        </div>
      )}
    </div>
    </MainLayout>
  );
}
